import type { EChartsOption } from "echarts";
import {
  buildAxisLabelStyle,
  buildTooltipStyle,
  buildTooltipValueStyle,
  CHART_FONT_FAMILY,
  chartGridLineColor,
  CONTAIN_AXIS_LABELS,
  formatImpressionAxis,
  getChartTextColors,
  IMPRESSION_FORMATTER,
} from "@/pages/CampaignOverviewPage/charts/chartHelpers";
import { getChartPalette } from "@/pages/CampaignOverviewPage/charts/chartPalette";
import {
  buildPillBarOption,
  formatCompactImpressions,
} from "@/pages/CampaignOverviewPage/charts/pillBarChart";
import { extractVideoStages } from "@/pages/CampaignOverviewPage/utils/videoFunnelStages";
import type { PerformanceReportRow } from "@/types/reporting";

const BAR_LABEL_WIDTH = 72;
const CURVE_SYMBOL_SIZE = 8;
const CURVE_STROKE_WIDTH = 2;
const CURVE_LABEL_FONT_SIZE = 11;
const CURVE_GRID = {
  bottom: 32,
  left: 24,
  right: 24,
  top: 24,
  ...CONTAIN_AXIS_LABELS,
} as const;

export const buildVideoBarOption = (
  rows: PerformanceReportRow[],
  isDark: boolean
): EChartsOption =>
  buildPillBarOption({
    formatValue: formatCompactImpressions,
    isDark,
    labelWidth: BAR_LABEL_WIDTH,
    rows: extractVideoStages(rows).map(({ label, value }) => ({
      label,
      value,
    })),
  });

// Flat plum line with white-stroked dots — no area fill, since the spec never
// lets a fill fade out to white.
export const buildVideoCurveOption = (
  rows: PerformanceReportRow[],
  isDark: boolean
): EChartsOption => {
  const axisLabelStyle = buildAxisLabelStyle(isDark);
  const { primary } = getChartTextColors(isDark);
  const valueStyle = buildTooltipValueStyle(isDark);
  const palette = getChartPalette(isDark);
  const stages = extractVideoStages(rows);

  return {
    grid: CURVE_GRID,
    series: [
      {
        data: stages.map(({ value }) => value),
        itemStyle: {
          borderColor: palette.card,
          borderWidth: 1.5,
          color: palette.primary,
        },
        label: {
          color: primary,
          fontFamily: CHART_FONT_FAMILY,
          fontSize: CURVE_LABEL_FONT_SIZE,
          formatter: (params: unknown) => {
            const item = params as { value: number };
            return IMPRESSION_FORMATTER.format(item.value);
          },
          position: "top",
          show: true,
        },
        lineStyle: { color: palette.primary, width: CURVE_STROKE_WIDTH },
        smooth: false,
        symbol: "circle",
        symbolSize: CURVE_SYMBOL_SIZE,
        type: "line",
      },
    ],
    tooltip: {
      ...buildTooltipStyle(isDark),
      formatter: (params: unknown) => {
        const items = params as { name: string; value: number }[];
        const item = items[0];
        return item
          ? `${item.name}: <b style="${valueStyle}">${IMPRESSION_FORMATTER.format(item.value)}</b>`
          : "";
      },
      trigger: "axis",
    },
    xAxis: {
      axisLabel: axisLabelStyle,
      axisLine: { lineStyle: { color: chartGridLineColor(isDark) } },
      axisTick: { show: false },
      data: stages.map(({ label }) => label),
      type: "category",
    },
    yAxis: {
      axisLabel: { ...axisLabelStyle, formatter: formatImpressionAxis },
      splitLine: { lineStyle: { color: chartGridLineColor(isDark) } },
      type: "value",
    },
  };
};
